/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./api/DataBehindTheVisAPI',
	'../../../lib/@waca/dashboard-common/dist/utils/ContentUtil',
	'../../../apiHelpers/SlotAPIHelper',
	'../../../features/content/dataQueryExecution/DataQueryUtils',
	'../../../dataSources/nls/StringResources'
], (_, StringResources, APIFactory, DataBehindTheVisAPI, ContentUtil, SlotAPIHelper, DataQueryUtils, DSStringResources) => {

	class DataBehindTheVis {

		constructor({ content, features }) {
			this.content = content;
			this.dashboard = features['Dashboard.API'];
			this.csvExport = features['Dashboard.CsvExport'];
			this.queryService = features['Dashboard.QueryService'];
			this.globalFilters = features['Dashboard.GlobalFilters'];
			this.eventGroups = features['Dashboard.EventGroups'];
			this.pageContextService = features['Dashboard.PageContextService'];
			this.visualization = features.Visualization;
			this.queryExec = features.DataQueryExecution;
		}

		getAPI() {
			if (!this._api) {
				this._api = APIFactory.createAPI(this, [DataBehindTheVisAPI]);
			}
			return this._api;
		}

		export(doAggregate = true) {
			return this.getData(doAggregate).then(data => {
				return this.csvExport.export(data.title, [data.headers], data.cells);
			});
		}


		destroy() {
			this.dashboardApi = null;
		}

		getData(doAggregate = true) {
			return this._queryForData(doAggregate).then(result => {
				const data = this._filterDataWithSelections(result);
				return {
					title: this._getTitle(),
					isAggregated: !!doAggregate,
					...data
				};
			});
		}

		_queryForData(doAggregate = true) {
			const datasource = this.visualization.getDataSource();
			if (datasource) {
				doAggregate = !!doAggregate;
				const extraColumnIds = this._getColumnIdsFromUnprojectedFilters();
				const hasMultiMeasures = this._isMultiMeasureVisualization(this.visualization);
				const hasExtraColumns = (!hasMultiMeasures && extraColumnIds && extraColumnIds.length > 0);
				const dataset = this._getQueryDataset();
				const mappingComplete = this.visualization.getSlots().isMappingComplete();
				const execute = hasExtraColumns ? this._getDataFromQuery(dataset, extraColumnIds, doAggregate) : this._getDataFromExecution(dataset, doAggregate, mappingComplete);
				return execute.then(result => {
					return this._getProcessedHeadersAndCells(result);
				});
			}
			// resolve with empty data
			return Promise.resolve({
				headers: [],
				cells: []
			});
		}

		_getTitle() {
			return StringResources.get('visExportFileName');
		}

		/**
		 * filtered data down to the vis data selection if there is any
		 */
		_filterDataWithSelections(data) {
			if (data.cells) {
				let filteredDataCells = [];
				const selections = this._getSelections();
				if (selections && selections.length) {
					_.each(data.cells, cell => {
						_.each(selections, selection => {
							let isSelectionInTheRow;
							const isTupleSetInCell = (valueFromTupleSet) => {
								return _.some(cell, cellValue => {
									// [249167] selections come in decorated as strings from vipr tuples
									// u is usually a string
									return (cellValue.value && cellValue.value.toString()) === valueFromTupleSet.u;
								});
							};
							if (selection.dataPoints) {
								// data points filters
								isSelectionInTheRow = _.some(selection.dataPoints, dataPoint => {
									return _.every((dataPoint), valueFromTupleSet => isTupleSetInCell(valueFromTupleSet));
								});

							} else {
								// edge filters
								isSelectionInTheRow = _.some(selection.edges, valueFromTupleSet => isTupleSetInCell(valueFromTupleSet));
							}
							// remove checking if entry already exists when live-widget changes for
							// brushing behaviour for table and indented list has been updated
							if (isSelectionInTheRow && !filteredDataCells.includes(cell)) {
								filteredDataCells.push(cell);
							}
						});
					});
					data.cells = filteredDataCells;
				}
			}
			return data;
		}

		/**
		 * Get the list of columnIds from unprojected local filters
		 * @returns {String[]}
		 */
		_getColumnIdsFromUnprojectedFilters() {
			const projectedDataItems = this._getVisibleDataItems();
			const columnIds = [];
			const dataSource = this.visualization.getDataSource();
			_.each(this._getFilterInfo(), filterEntries => {
				_.each(filterEntries, filterEntry => {
					// Get itemId if exists; Otherwise get columnId from filter entry
					// Edge filter entry does not have itemId; it has only columnId that serves the same purpurse as itemId
					// Data point filter has itemId but it does not have columnId.
					// see more _decoratePageContext function in visFilterSupport
					this._getUnProjectedItemsFromFilterEntry(_.pluck(projectedDataItems, 'itemId'), filterEntry, columnIds, dataSource);
				});
			});
			return columnIds;
		}

		_isMultiMeasureVisualization() {
			return !!_.find(this.visualization.getSlots().getMappedSlotList(), slot => SlotAPIHelper.isMultiMeasuresSeriesSlot(slot));
		}

		_getQueryDataset() {
			const definition = this.visualization.getDefinition();
			const datasetList = definition.getDatasetList();
			// If there is only 1 data set then use it's id, otherwise, return the default id
			return (datasetList && datasetList.length === 1) ? datasetList[0].id : definition.getDefaultDatasetId();
		}

		_getDataFromQuery(dataset, extraColumnIds, doAggregate) {
			const datasource = this.visualization.getDataSource();
			const queryService = this.dashboard.getFeature('QueryService');
			const queryType = doAggregate ? queryService.TYPE.DEFAULT : queryService.TYPE.DETAIL;
			const query = queryService.createQuery(queryType);

			// setup datasource and projected data items
			query.setDataSource(datasource.getId());
			query.addDataItems(this._getDataItems(dataset));

			// append extra data items
			query.addColumns(extraColumnIds);

			// apply all filters
			const filterInfo = DataQueryUtils.removeForecastFilters(this._getFilterInfo());
			const filterList = filterInfo.localFilters;
			filterList.push(...filterInfo.selectionFilters, ...filterInfo.globalFilters);
			query.addFilters(filterList);

			return query.executeQuery();
		}

		_getDataFromExecution(dataset, doAggregate, mappingComplete) {
			if (doAggregate) {
				const queryResults = this.queryExec.getCurrentQueryResults();

				if (!mappingComplete || _.isEmpty(queryResults)) {
					return Promise.resolve(false);
				}

				// @todo use new query results api
				if (queryResults.getImplType && queryResults.getInterfaceType) {
					return Promise.resolve(queryResults.getResult(dataset));
				}

				if (dataset) {
					return Promise.resolve(queryResults.getQueryResultByDataViewId(dataset).getQueryResult());
				}
				return Promise.resolve(queryResults.getDefaultQueryResult().getQueryResult());
			} else {
				// submit a detail query to obtain the disaggregated result
				const results = this.queryExec.executeQueries('detail');
				//return results.getResult(dataset);
				return results.then(_results => {
					return _results.getResult(dataset);
				});
			}
		}

		_getVisibleDataItems() {
			const dataItems = [];
			const nestList = [];
			const slots = this.visualization.getSlots();
			_.each(slots.getMappedSlotList(), function(slot) {
				_.each(slot.getDataItemList(), function(dataItem, index) {
					if (!SlotAPIHelper.isMultiMeasuresSeriesOrValue(slot, index)) {
						dataItems.push({
							id: dataItem.getId(),
							itemId: dataItem.getColumnId()
						});
						//keep track of the list of dataItems assigned to a slot.
						//If a slot has multiple items and the slot is type stacked,
						//use this list as the nestList of a stacked dataItem (see below)
						nestList.push(dataItem.getId());
					}
				}.bind(this));
				if (nestList.length > 1 && slot.isStacked()) {
					//If the slot is stacked, produce a dataItem whose name matches the id
					//with nested items whose names match the dataItems.
					const datasetIds = slot.getDefinition().getDatasetIdList();
					datasetIds.forEach(datasetId => {
						dataItems.push({
							id: slot.getId(),
							nest: nestList,
							layerId: datasetId
						});
					});
				}
			});
			return dataItems;
		}

		_getUnProjectedItemsFromFilterEntry(projectedDataItemIDs, filterEntry, aUnProjectedItemsFromFilterEntry, dataSource) {
			const filterEntryItemId = filterEntry.itemId || filterEntry.columnId;
			const column = dataSource.getMetadataColumn(filterEntryItemId);
			// Skip model filter columns
			if (!column || column.getObjectType() !== 'Filter') {
				if (filterEntryItemId) {
					if(projectedDataItemIDs.indexOf(filterEntryItemId) === -1 && aUnProjectedItemsFromFilterEntry.indexOf(filterEntryItemId) === -1) {
						aUnProjectedItemsFromFilterEntry.push(filterEntryItemId);
					}
				} else {
					_.each(filterEntry.values, function(valueExpr) {
						this._getUnProjectedItemsFromFilterEntry(projectedDataItemIDs, valueExpr, aUnProjectedItemsFromFilterEntry, dataSource);
					}.bind(this));
				}
			}
		}

		_getFilterInfo() {
			const datasource = this.visualization.getDataSource();
			const localFilters = this.visualization.getLocalFilters().getFilterList();
			const type = this.dashboard.getAppConfig('pageContainerType');
			const globalFilters = this.globalFilters.getFilterList({
				origin: 'filter',
				sourceId: datasource.getId(),
				scope: ContentUtil.getPageContent(this.content, type).getId(),
				eventGroupId: this.eventGroups.getGroupId(this.content.getId())
			});

			const selectionFilters = this.globalFilters.getFilterList({
				origin: 'visualization',
				sourceId: datasource.getId(),
				scope: ContentUtil.getPageContent(this.content, type).getId(),
				eventGroupId: this.eventGroups.getGroupId(this.content.getId()),
				eventSourceId: this.content.getId()
			});

			return { localFilters, globalFilters, selectionFilters };
		}

		_getProcessedHeadersAndCells(queryResult) {
			let dataHeaders = [];
			let dataCells = [];
			const usedMetadataColumns = this._getMetadataColumnList();

			if (queryResult) {
			/** when the widget is in not rendered state (eg. missing required slots), removing a slot does not do another query
			 * the cached data will have one or more data row(s) extra
			 * Hence we filter down to the only used meta data columns */
				let usedIds = this._getUsedMetadataColumnsIncluldingFiltersIds(usedMetadataColumns);

				const resultItemList = queryResult.getResultItemList();
				let unusedColumnIndices = [];
				_.each(resultItemList, function(item) {
					_.each(item.getDataItemList(), (dataItem, index) => {
						if(usedIds.indexOf(dataItem.getColumnId()) !== -1) {
							dataHeaders.push(dataItem.getLabel());
						} else {
							unusedColumnIndices.push(index);
						}
					});
				}.bind(this));

				const firstRowLevels = [];
				const columnCount = this._getColumnCount(resultItemList);
				for (let iRow = 0; iRow < queryResult.getRowCount(); iRow++) {
					let skipRow = false;
					let dataRow = [];
					for (let iCol = 0; iCol < columnCount; iCol++) {
						if (unusedColumnIndices.indexOf(iCol) === -1) {
							const value = queryResult.getValue(iRow, iCol);
							if (Array.isArray(value)) {
								dataRow.push.apply(dataRow, value.map(dataCellObj => {
									dataCellObj.toString = this._getCellValue.bind(this, dataCellObj);
									return dataCellObj;
								}));
								if (iRow === 0) {
								// capture the level from the first row including all nested values
									firstRowLevels[iCol] = _.pluck(value, 'ln');
								} else {
								// skip the row if at least one nested value level is deeper than the first row - expanded row
									skipRow = skipRow || _.some(value, (v, offset) => firstRowLevels[iCol][offset] < v.ln );
								}
							} else {
								value.toString = this._getCellValue.bind(this, value);
								dataRow.push(value);
							}
							// skip thw row if the value is a summary type
							skipRow = skipRow || value.type === (queryResult.VALUE_TYPE && queryResult.VALUE_TYPE.SUMMARY);
						}
					}
					if (!skipRow) {
						dataCells.push(dataRow);
					}
				}
			}

			return {
				headers: dataHeaders,
				cells: dataCells
			};
		}

		/** get an array of used meta data column, filters ids */
		_getUsedMetadataColumnsIncluldingFiltersIds(usedMetadataColumns){
			var ids = [];
			_.each(usedMetadataColumns, function(dataColumn){
				if(dataColumn && !dataColumn.isMissing()){
					ids.push(dataColumn.getId());
				}
			});
			var extraColumnIdsFromNonProjectedFilters = this._getColumnIdsFromUnprojectedFilters();
			ids.push(...extraColumnIdsFromNonProjectedFilters);

			// add extra virtual meta data column resulted by multi measure post processing
			const hasMultiMeasures = this._isMultiMeasureVisualization(this.visualization);
			if (hasMultiMeasures) {
				ids.push.apply(ids, ['_multiMeasuresValue', '_multiMeasuresSeries']);
			}
			return ids;
		}

		_getMetadataColumnList() {
			const slots = this.visualization.getSlots();
			return _.map(slots.getDataItemList(), dataItem => dataItem.getMetadataColumn());
		}

		_getColumnCount(queryResultItemList) {
			return _.chain(queryResultItemList)
				.map((resultItem) => {
					const isMeasure = _.every(resultItem.getDataItemList(), (dataItem) => dataItem.getAggregation() && dataItem.getType() === 'fact');
					// when multiple measures are mapped to a slot, V5 result item will represent the measures as a single set
					const isMultiMeasure = isMeasure && (resultItem.getDataItemList().length > resultItem.getValue(0).length);
					return isMultiMeasure ? resultItem.getDataItemList() : resultItem;
				})
				.flatten().value().length;
		}

		_getCellValue(dataCell) {
			const isMeasure = this._isCellValueMeasure(dataCell);
			const value = isMeasure ? dataCell.value : dataCell.label;
			if (value === null) {
				return DSStringResources.get('nullValueText');
			} else if (isMeasure) {
				// round numbers types to at most 2 digits
				return Math.round(value * 100) / 100;
			} else {
				return value;
			}
		}

		_isCellValueMeasure(dataCell) {
			return dataCell && _.isUndefined(dataCell.label);
		}

		_getDataItems(dataset) {
			const slots = this.visualization.getSlots();

			if (dataset) {
				const slotMapping = slots.getMappingInfoList();
				let dataItems = [];
				slotMapping.forEach(item => {
					if (item.slot.getDefinition().getDatasetIdList().includes(dataset)) {
						dataItems.push(item.dataItem);
					}
				});
				return dataItems;
			} else {
				return slots.getDataItemList();
			}
		}

		_getSelections() {
			const selections = [];
			const pageContext = this.pageContextService.getPageContext();

			_.each(pageContext && pageContext.getPageContextItems(this._getItemContext()), function(pageContextItem) {
				if (pageContextItem.getDataPoints() && pageContextItem.getDataPoints().length) {
					selections.push({
						dataPoints: pageContextItem.getDataPoints()
					});
				} else if (pageContextItem.getValues() && pageContextItem.getValues().length) {
					selections.push({
						edges: pageContextItem.getValues()
					});
				}
			});
			return selections;
		}

		_getItemContext() {
			const type = this.dashboard.getAppConfig('pageContainerType');
			if (!this.visualization) {
				return null;
			}
			return {
				origin: 'visualization',
				sourceId: this.visualization.getDataSource() && this.visualization.getDataSource().getId(),
				scope: ContentUtil.getPageContent(this.content, type).getId(),
				eventGroupId: this.eventGroups.getGroupId(this.content.getId()),
				//use the 'strict match option' and event source to only select brushing entries
				//that originate from this visualization.
				eventSourceId: this.content.getId(),
				_strictMatch: true
			};
		}

	}

	return DataBehindTheVis;

});
